import cx from 'clsx';
import { useRouter } from 'next/router';
import { trackEvent } from '@kcd/event-tracker';

import PlacementRenderer from '~/domains/mmp/components/PlacementRenderer';
import { usePlacementBanners } from '~/domains/mmp/queries';
import useScrollDirectionWithY from '~/hooks/useScrollDirectionWithY';
import { useBusinessIdStore } from '~/store/global/useBusinessIdStore';
import useMarketFloatingStore from '~/store/global/useMarketFloatingStore';
import type { OAISchema } from '~/types/openapi';
import { openPageModal } from '~/utils/mobileApp';

import PrefixLogo from './PrefixLogo';
import type { Variant } from '../utils';
import { getFloatingStyles } from '../utils';

import { FloatingDismissIcon } from '~/assets/icons/card_banner';

interface Props {
  data: OAISchema['PlacementBannerResponse'];
  pageName?: string;
  classNames?: string;
}

const MarketCardFloatingContent = ({ data, pageName, classNames }: Props) => {
  const router = useRouter();
  const { setClose } = useMarketFloatingStore();
  const { variant, content, landingUrl } = data;

  const onClick = () => {
    void trackEvent('clicked 캐시노트마켓/floating', {
      pageName,
      pathname: router.pathname,
    });

    if (landingUrl) {
      openPageModal(landingUrl);
    }
  };

  const onClose = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    setClose();
  };

  return (
    <div
      className={cx(
        'fixed top-[76px] right-0 left-0 flex justify-center mx-auto max-w-[768px] px-20 z-[25]',
        classNames,
      )}
    >
      <div
        className={cx(
          'inline-flex items-center gap-[6px] px-16 py-[10px] rounded-[30px] justify-between shadow-[0px_4px_8px_0px_#0000001A]',
          getFloatingStyles(variant as unknown as Variant).banner,
        )}
        role="presentation"
        onClick={onClick}
      >
        <span className="size-[22px] shrink-0">
          <PrefixLogo variant={variant as unknown as Variant} />
        </span>
        <span
          className={cx(
            'typo-paragraph200-semibold overflow-hidden text-ellipsis whitespace-nowrap',
            getFloatingStyles(variant as unknown as Variant).title,
          )}
        >
          {content}
        </span>
        <span
          className={cx(
            'flex-shrink-0 w-16 h-16 ml-[2px]',
            getFloatingStyles(variant as unknown as Variant).close,
          )}
          role="presentation"
          onClick={onClose}
        >
          <FloatingDismissIcon />
        </span>
      </div>
    </div>
  );
};

const MarketCardFloating = ({
  pageName,
  classNames,
}: {
  pageName?: string;
  classNames?: string;
}) => {
  const { isVisible } = useMarketFloatingStore();
  const { businessId } = useBusinessIdStore();
  const { scrollY } = useScrollDirectionWithY();
  const { data } = usePlacementBanners(
    businessId,
    'market_card_banner_with_floating',
  );

  if (!data || !data.length || !isVisible || scrollY === 0) return <></>;

  return (
    <PlacementRenderer {...data[0]} className="mt-20">
      <MarketCardFloatingContent
        data={data[0]}
        pageName={pageName}
        classNames={classNames}
      />
    </PlacementRenderer>
  );
};

export default MarketCardFloating;
